// Find job type option by its value
export function findTypeByValue(jobTypeOptions, value) {
  if (!Array.isArray(jobTypeOptions)) return undefined;
  return jobTypeOptions.find((r) => r.value === value);
}

export const DEFAULT_TAP_JOBS_STATUS = "RUNNING";

/**
 * Default TAP Jobs filter: Running status, no job type selected.
 * @param {Array} jobStatusOptions
 * @returns {Object|null} Filter form slice or null if Running is unavailable
 */
export function getDefaultTapJobsFilter(jobStatusOptions) {
  if (!Array.isArray(jobStatusOptions) || jobStatusOptions.length === 0) {
    return null;
  }

  const defaultJobStatus =
    findTypeByValue(jobStatusOptions, DEFAULT_TAP_JOBS_STATUS) ??
    jobStatusOptions.find((option) => option.label === "Running");

  if (!defaultJobStatus) {
    return null;
  }

  return {
    jobType: "",
    jobStatus: [defaultJobStatus],
  };
}

/**
 * Returns true when URL params omit TAP Jobs filter fields.
 * @param {Object} params
 * @returns {boolean}
 */
export function hasTapJobsFilterParams(params) {
  return !!(params.jobType || params.jobStatus || params.jobId);
}

/**
 * On first load without URL filter params, defer fetching until default filters
 * are applied in useAsyncEffect. Avoids a race where an unfiltered request
 * completes after the filtered one and shows wrong statuses in the grid.
 * @param {Object} params - Parsed URL search params
 * @param {boolean} isInitialMount
 * @returns {boolean}
 */
export function shouldDeferInitialTapJobsFetch(params, isInitialMount) {
  return isInitialMount && !hasTapJobsFilterParams(params);
}

// Find multiple options by their values (for multi-selects)
export function findOptionsByValues(options, values) {
  if (!Array.isArray(options) || !values) return [];
  const valueArray = Array.isArray(values)
    ? values
    : values.split(",").map((v) => v.trim());
  return valueArray
    .map((val) => options.find((opt) => String(opt.value) === String(val)))
    .filter(Boolean);
}

/**
 * Build filter data object from URL search params
 * @param {Object} params - Parsed URL search params
 * @param {Array} jobTypeOptions - Available job type options
 * @param {Array} jobStatusOptions - Available job status options
 * @returns {Object} Filter data object for the form
 */
export function buildFilterFromParams(
  params,
  jobTypeOptions,
  jobStatusOptions,
) {
  const filterData = {};

  if (params.jobId) {
    filterData.jobId = params.jobId;
  }

  if (params.jobType && jobTypeOptions.length > 0) {
    const matched = findTypeByValue(jobTypeOptions, params.jobType);
    if (matched) filterData.jobType = matched;
  }

  if (params.jobStatus && jobStatusOptions.length > 0) {
    filterData.jobStatus = findOptionsByValues(
      jobStatusOptions,
      params.jobStatus,
    );
  }

  if (params.pageSize) {
    filterData.pageSize = Number(params.pageSize);
  }

  return filterData;
}

// Serialize multi-select value to comma-separated string of IDs
function serializeMultiSelect(value) {
  if (!value || (Array.isArray(value) && value.length === 0)) return "";
  if (Array.isArray(value)) {
    return value.map((v) => v?.value ?? v).join(",");
  }
  return value?.value ?? value ?? "";
}

// Cleans the job filter object by removing empty or null values
export function cleanJobFilter(filter, tapJobRange, tapJobSort) {
  const filtered = {};

  // Text fields
  if (filter.jobId) filtered.jobId = filter.jobId;

  // Single select - serialize to value
  if (filter.jobType && typeof filter.jobType === "object") {
    filtered.jobType = filter.jobType.value ?? filter.jobType.id ?? "";
  } else if (filter.jobType) {
    filtered.jobType = filter.jobType;
  }

  // Multi-select fields
  const jobStatusVal = serializeMultiSelect(filter.jobStatus);
  if (jobStatusVal) filtered.jobStatus = jobStatusVal;

  // Pagination
  filtered.tapJobRange = tapJobRange;
  filtered.tapJobSort = tapJobSort;
  if (filter.pageSize) filtered.pageSize = filter.pageSize;

  return filtered;
}
